import * as signalR from "@microsoft/signalr";
import { v4 as uuidv4 } from "uuid";
import AuthProvider from "shared/authProvider";
import { shouldAuthenticate } from "utils/auth";
export default class WSService {
    static instance;
    connection;
    ready;
    requestId;
    clientId;
    constructor() {
        if (WSService.instance) {
            throw new Error("Instantiation failed: use WSService.getInstance() instead of new");
        }
        this.ready = false;
        this.requestId = 1;
        WSService.instance = this;
    }
    static getInstance() {
        return WSService.instance ?? new WSService();
    }
    createSignalRConnection = (url) => {
        const shouldAuth = shouldAuthenticate();
        const connectionOptions = {
            transport: signalR.HttpTransportType.WebSockets,
        };
        if (shouldAuth) {
            console.log("🔐 Adding token factory for authenticated connection");
            connectionOptions.accessTokenFactory = async () => {
                const auth = await AuthProvider.getInstance();
                return await auth.getAccessToken();
            };
        }
        else {
            console.log("🏠 On-premise mode - no authentication required");
        }
        const connection = new signalR.HubConnectionBuilder()
            .withUrl(url, connectionOptions)
            .configureLogging(signalR.LogLevel.Information)
            .withAutomaticReconnect()
            .build();
        connection.keepAliveIntervalInMilliseconds = 5000;
        connection.serverTimeoutInMilliseconds = 30000;
        return connection;
    };
    init(url) {
        if (!WSService.instance) {
            return;
        }
        this.connection = this.createSignalRConnection(url);
        this.connection.onreconnecting((err) => {
            console.warn("SignalR onreconnecting", err);
            this.ready = false;
        });
        this.connection.onreconnected((connectionId) => {
            console.log("SignalR onreconnected", connectionId);
            this.ready = true;
        });
        this.connection.onclose((err) => {
            console.error("SignalR onclose", err);
            this.ready = false;
        });
    }
    async start() {
        if (this.ready)
            return;
        if (!this.connection)
            throw new Error("Connection is not initialized");
        try {
            await this.connection.start();
            this.ready = true;
            this.clientId = uuidv4();
            console.log("SignalR started, clientId:", this.clientId);
        }
        catch (e) {
            this.ready = false;
            console.error("SignalR start failed", e);
            throw e;
        }
    }
    async stop() {
        if (!this.connection)
            return;
        try {
            await this.connection.stop();
        }
        catch (e) {
            console.warn("SignalR stop threw", e);
        }
        finally {
            this.ready = false;
        }
    }
    async send(method, rawData, ...args) {
        if (!this.connection)
            return;
        const data = { id: this.requestId++, ...rawData };
        try {
            await this.ensureConnectedOrWait();
            return await this.connection.send(method, data, ...args);
        }
        catch (e) {
            console.error("SignalR send failed", e, { method, data });
        }
    }
    async rsInvoke(method, requestId, payload) {
        if (!this.connection)
            return;
        const data = { id: requestId ?? this.requestId, ...payload };
        if (!requestId)
            this.requestId++;
        try {
            await this.ensureConnectedOrWait();
            return await this.connection.invoke(method, data);
        }
        catch (e) {
            console.error("SignalR invoke failed", e, { method, data });
            throw e;
        }
    }
    subscribeMethod(method, handler) {
        if (!this.connection)
            return false;
        this.connection.on(method, handler);
        return true;
    }
    unsubscribeMethod(method, handler) {
        if (!this.connection)
            return false;
        this.connection.off(method, handler);
        return true;
    }
    subscribeReconnectingHandler(handler) {
        if (!this.connection)
            return false;
        this.connection.onreconnecting(handler);
        return true;
    }
    subscribeReconnectedHandler(handler) {
        if (!this.connection)
            return false;
        this.connection.onreconnected(handler);
        return true;
    }
    subscribeCloseHandler(handler) {
        if (!this.connection)
            return false;
        this.connection.onclose((err) => {
            console.error("SignalR onclose called. err:", err);
            if (err?.message)
                console.error("onclose message:", err.message);
            if (err?.stack)
                console.error("onclose stack:", err.stack);
            this.ready = false;
            handler(err);
        });
        return true;
    }
    getLastRequestId() {
        return this.requestId - 1;
    }
    getCurrentRequestId() {
        return this.requestId;
    }
    getClientId() {
        return this.clientId;
    }
    async ensureConnectedOrWait() {
        if (!this.connection)
            throw new Error("Not initialized");
        if (this.connection.state === signalR.HubConnectionState.Connected) {
            return;
        }
        await this.connection.start();
    }
}
